import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SavingsIcon from '@mui/icons-material/Savings';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Box, Container, Grid, Paper, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';

const advantages = [
    {
        icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
        title: 'Yapay Zeka Destekli',
        description: 'Açık kaynak tabanlı yapay zeka teknolojisi ile milyonlarca ürün ve kategoriye sahip e-ticaret soru-cevapları ile eğitildi. Müşteri sorularını temsilci olarak yanıtlar, manuel iş yükünü %90 azaltır.'
    },
    {
        icon: <SpeedIcon sx={{ fontSize: 40 }} />,
        title: 'Hızlı Yanıt',
        description: 'Müşteri sorularını saniyeler içinde yanıtlayarak müşteri memnuniyetini artırır ve satışları hızlandırır.'
    },
    {
        icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
        title: 'Satış Artışı',
        description: 'Hızlı ve doğru yanıtlar sayesinde müşteri memnuniyeti ve satış oranlarınızda %30\'a varan artış sağlar.'
    },
    {
        icon: <SavingsIcon sx={{ fontSize: 40 }} />,
        title: 'Maliyet Tasarrufu',
        description: 'Müşteri hizmetleri maliyetlerinizi %70\'e varan oranda düşürür, operasyonel verimliliği artırır.'
    },
    {
        icon: <SentimentSatisfiedAltIcon sx={{ fontSize: 40 }} />,
        title: 'Müşteri Memnuniyeti',
        description: '7/24 kesintisiz hizmet ve hızlı yanıtlar ile müşteri memnuniyetini en üst seviyeye çıkarır.'
    },
    {
        icon: <WorkspacePremiumIcon sx={{ fontSize: 40 }} />,
        title: 'Kalite Kontrolü',
        description: 'Tüm yanıtlar kalite kontrol süzgecinden geçer, yapay zeka ile oluşturulan yanıtlar sadece güvenli ve kaliteli olduğunda size iletilir. Bu sayede marka imajınızı ve müşteri iletişiminizi güçlendirir.'
    }
];

export default function AdvantagesSection() {
    const theme = useTheme();

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: 'white',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Container maxWidth="lg">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                fontWeight: 700,
                                color: theme.palette.secondary.main,
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -10,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 60,
                                    height: 4,
                                    backgroundColor: theme.palette.primary.main,
                                    borderRadius: 2
                                }
                            }}
                        >
                            Neden <Box component="span" sx={{ color: theme.palette.primary.main }}>TrendAsist</Box>?
                        </Typography>
                        <motion.div
                            animate={{
                                y: [0, -10, 0],
                            }}
                            transition={{
                                duration: 1,
                                repeat: Infinity,
                                repeatType: "reverse",
                                ease: "easeInOut"
                            }}
                            style={{
                                marginLeft: '8px',
                                fontSize: '2rem',
                                lineHeight: 1
                            }}
                        >
                            🚀
                        </motion.div>
                    </Box>
                    <Typography
                        variant="h6"
                        align="center"
                        sx={{
                            mb: 8,
                            color: theme.palette.text.secondary,
                            maxWidth: '800px',
                            mx: 'auto'
                        }}
                    >
                        E-ticaret operasyonlarınızı otomatikleştirin, müşteri memnuniyetini artırın ve maliyetlerinizi düşürün
                    </Typography>
                </motion.div>

                <Grid container spacing={4}>
                    {advantages.map((advantage, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <Paper
                                    elevation={0}
                                    component={motion.div}
                                    whileHover={{
                                        scale: 1.02,
                                        boxShadow: '0 8px 40px rgba(0,0,0,0.1)'
                                    }}
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        borderRadius: 4,
                                        backgroundColor: 'white',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            backgroundColor: 'white',
                                            '& .MuiSvgIcon-root': {
                                                color: theme.palette.primary.main
                                            }
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            mb: 2,
                                            color: theme.palette.secondary.main,
                                            transition: 'color 0.3s ease'
                                        }}
                                    >
                                        {advantage.icon}
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: theme.palette.secondary.main
                                        }}
                                    >
                                        {advantage.title}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {advantage.description}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Dekoratif arka plan elementleri */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 40,
                    left: -100,
                    width: 200,
                    height: 200,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}22, ${theme.palette.primary.main}11)`,
                    borderRadius: '50%',
                    filter: 'blur(60px)',
                    zIndex: 0
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -60,
                    right: -60,
                    width: 180,
                    height: 180,
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}22, ${theme.palette.secondary.main}11)`,
                    borderRadius: '50%',
                    filter: 'blur(50px)',
                    zIndex: 0
                }}
            />
        </Box>
    );
} 